import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ToggleSwitch, CustomDatePicker } from "@components/Forms";
import CustomRadio from "@components/Forms/CustomRadio";
import CustomTextarea from "@components/Forms/CustomTextarea";
import PrimaryButton from "@components/Buttons/PrimaryButton";
import OutlineButton from "@components/Buttons/OutlineButton";
import { useTheme } from "@hooks/useTheme";
import { IMPORT_TYPE, DATE_TYPE } from "@constants/index";

const ImportProjectSettingModal = ({
  isOpen,
  onClose,
  onImportSuccess,
  "data-testid": dataTestId = "import-project-setting-modal",
}) => {
  const fieldContainerTestId = (name) => `${dataTestId}-field-${name}-container`;
  const fieldLabelTestId = (name) => `${dataTestId}-field-${name}-label`;
  const fieldInputTestId = (name) => `${dataTestId}-field-${name}-input`;
  const fieldErrorTestId = (name) => `${dataTestId}-field-${name}-error`;
  const { t } = useTranslation();
  const { colors } = useTheme();
  const [ignoreExisting, setIgnoreExisting] = useState(false);
  const [importType, setImportType] = useState(null);
  const [dateType, setDateType] = useState(null);
  const [customDateRange, setCustomDateRange] = useState({
    startDate: null,
    endDate: null,
  });
  const [projectCodes, setProjectCodes] = useState("");
  const [errors, setErrors] = useState({
    importType: "",
    dateRange: "",
    projectCode: "",
  });

  useEffect(() => {
    if (!isOpen) return;

    setIgnoreExisting(false);
    setImportType(null);
    setDateType(null);
    setCustomDateRange({ startDate: null, endDate: null });
    setProjectCodes("");
    setErrors({
      importType: "",
      dateRange: "",
      projectCode: "",
    });
  }, [isOpen]);

  /* Reset date state if not DATE_RANGE */
  useEffect(() => {
    if (importType !== IMPORT_TYPE.DATE_RANGE) {
      setDateType(null);
      setCustomDateRange({ startDate: null, endDate: null });
    }
  }, [importType]);

  if (!isOpen) return null;

  const formatStartOfDay = (date, addDay = false) => {
    if (!date) return null;
    const d = new Date(date);
    if (addDay) d.setDate(d.getDate() + 1);

    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");

    return `${yyyy}-${mm}-${dd} 00:00:00`;
  };

  const dateCalculators = {
    [DATE_TYPE.LAST_7_DAYS]: () => {
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - 6);
      return {
        startDate: formatStartOfDay(start),
        endDate: formatStartOfDay(end, true),
      };
    },

    [DATE_TYPE.THIS_MONTH]: () => {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      return {
        startDate: formatStartOfDay(start),
        endDate: formatStartOfDay(now, true),
      };
    },

    [DATE_TYPE.CUSTOM]: () => ({
      startDate: formatStartOfDay(customDateRange.startDate),
      endDate: formatStartOfDay(customDateRange.endDate, true),
    }),
  };

  const splitProjectCodes = (value) =>
    value
      .split(/(?:,\s+|\n)+/)
      .map(c => c.trim())
      .filter(Boolean);

  const hasDuplicateCodes = (codes) => {
    const set = new Set();
    for (const code of codes) {
      const key = code.toLowerCase();
      if (set.has(key)) return true;
      set.add(key);
    }
    return false;
  };

  const handleSubmit = () => {
    const newErrors = {
      importType: "",
      dateRange: "",
      projectCode: "",
    };

    if (!importType) {
      newErrors.importType = t("preferences.please-select-import-option");
    }

    if (importType === IMPORT_TYPE.DATE_RANGE) {
      if (!dateType) {
        newErrors.dateRange = t("preferences.please-select-date-condition");
      }
      if (
        dateType === DATE_TYPE.CUSTOM &&
        (!customDateRange.startDate || !customDateRange.endDate)
      ) {
        newErrors.dateRange = t("preferences.please-select-custom-range");
      }
    }

    let projectCodesList = null;

    if (importType === IMPORT_TYPE.PROJECT_CODE) {
      projectCodesList = splitProjectCodes(projectCodes);

      if (!projectCodesList.length) {
        newErrors.projectCode = t("preferences.project-codes-cannot-empty");
      } else if (hasDuplicateCodes(projectCodesList)) {
        newErrors.projectCode = t("preferences.project-codes-duplicate");
      }
    }
    if (
      newErrors.importType ||
      newErrors.dateRange ||
      newErrors.projectCode
    ) {
      setErrors(newErrors);
      return;
    }

    let startDateStr = null;
    let endDateStr = null;
    let startDate = null;
    let endDate = null;

    if (importType === IMPORT_TYPE.DATE_RANGE && dateType) {
      const calc = dateCalculators[dateType];
      if (calc) {
        const dates = calc();
        startDateStr = dates.startDate;
        endDateStr = dates.endDate;
        startDate = customDateRange.startDate || new Date(dates.startDate);
        endDate = customDateRange.endDate || new Date(dates.endDate);
      }
    }

    const payload = {
      importType,
      ignoreExistingProject: ignoreExisting ? "Y" : "N",
      projectCodes: projectCodesList,
      startDateStr,
      endDateStr,
      startDate,
      endDate,
    };

    onImportSuccess(payload);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className={`fixed inset-0 z-50 flex items-center ${colors.bgImportOverlay} justify-start md:justify-center overflow-x-auto p-4`} data-testid={`${dataTestId}-overlay`}>
      <div className={`w-full max-w-3xl min-w-3xl ${colors.bgModal} rounded-lg ${colors.borderPrimary}`} data-testid={dataTestId}>
        {/* HEADER */}
        <div className={`p-6 ${colors.borderDialogInactive}`} data-testid={`${dataTestId}-header`}>
          <h2 className={`${colors.textPrimary} text-xl font-bold`} data-testid={`${dataTestId}-title`}>
            {t("preferences.import-project-setting")}
          </h2>
        </div>

        {/* BODY */}
        <div className="p-6 space-y-6" data-testid={`${dataTestId}-content`}>
          {/* Preconditions */}
          <section data-testid={fieldContainerTestId("ignore-existing")}>
            <h3 className={`text-md font-medium ${colors.textPrimary} mb-4`}>
              {t("preferences.pre-conditions")}
            </h3>
            <div className="flex items-center justify-between">
              <label className={`text-sm font-medium ${colors.textSecondary}`} data-testid={fieldLabelTestId("ignore-existing")}>
                {t("preferences.ignore-existing-projects")}
              </label>
              <div className="flex items-center" data-testid={fieldInputTestId("ignore-existing")}>
                <ToggleSwitch
                  checked={ignoreExisting}
                  onChange={() => setIgnoreExisting(!ignoreExisting)}
                />
                <span className={`text-sm ml-1 ${colors.textPrimary}`}>
                  {ignoreExisting ? t("common.yes") : t("common.no")}
                </span>
              </div>
              <p className={`text-sm ${colors.textSecondary} italic max-w-[350px]`}>
                {t("preferences.text-note-ignore")}
              </p>
            </div>
          </section>

          {/* Import Conditions */}
          <section className="space-y-4">
            <h3 className={`text-md font-medium ${colors.textPrimary} mb-4`}>
              {t("preferences.import-by-condition")}
            </h3>

            {/* ALL */}
            <div
              data-testid={fieldContainerTestId("import-type-all")}
              className={`p-4 border rounded-lg ${importType === IMPORT_TYPE.ALL
                ? colors.borderSelectedOption
                : colors.borderDialogInactive
                }`}
              onClick={() => {
                setImportType(IMPORT_TYPE.ALL);
                setErrors(prev => ({ ...prev, importType: "", dateRange: "", projectCode: "" }));
              }}
            >
              <CustomRadio
                name="importType"
                value={IMPORT_TYPE.ALL}
                checked={importType === IMPORT_TYPE.ALL}
                onChange={() => {
                  setImportType(IMPORT_TYPE.ALL);
                  setErrors(prev => ({ ...prev, importType: "", dateRange: "", projectCode: "" }));
                }}
                label={t("preferences.all-fossid-projects")}
                className="text-base"
              />
              <p className={`pt-2 pl-6 text-sm ${colors.textSecondary} italic`}> {t("preferences.text-note-all-option")} </p>
            </div>

            {/* DATE RANGE */}
            <div
              data-testid={fieldContainerTestId("import-type-date-range")}
              className={`p-4 border rounded-lg space-y-3 ${importType === IMPORT_TYPE.DATE_RANGE
                ? colors.borderSelectedOption
                : colors.borderDialogInactive
                }`}
            >
              <CustomRadio
                name="importType"
                value={IMPORT_TYPE.DATE_RANGE}
                checked={importType === IMPORT_TYPE.DATE_RANGE}
                onChange={() => {
                  setImportType(IMPORT_TYPE.DATE_RANGE);
                  setErrors(prev => ({ ...prev, importType: "", dateRange: "", projectCode: "" }));
                }}
                label={t("preferences.date-range")}
              />

              <div className="flex items-center gap-2 flex-wrap">
                <button
                  type="button"
                  data-testid={fieldInputTestId("date-last-7-days")}
                  onClick={() => {
                    setDateType(DATE_TYPE.LAST_7_DAYS);
                    setImportType(IMPORT_TYPE.DATE_RANGE);
                    setErrors(prev => ({ ...prev, dateRange: "", importType: "", projectCode: "" }));
                  }}
                  className={`px-4 py-1 rounded-full text-sm ${colors.textSecondary} border ${dateType === DATE_TYPE.LAST_7_DAYS
                    ? `${colors.borderSelectedOption} ${colors.bgSelectedCyanSubtle}`
                    : colors.borderMuted
                    }`}
                >
                  {t("status.last-7-days")}
                </button>

                <button
                  type="button"
                  data-testid={fieldInputTestId("date-this-month")}
                  onClick={() => {
                    setDateType(DATE_TYPE.THIS_MONTH);
                    setImportType(IMPORT_TYPE.DATE_RANGE);
                    setErrors(prev => ({ ...prev, dateRange: "", importType: "", projectCode: "" }));
                  }}
                  className={`px-4 py-1 rounded-full text-sm ${colors.textSecondary} border ${dateType === DATE_TYPE.THIS_MONTH
                    ? `${colors.borderSelectedOption} ${colors.bgSelectedCyanSubtle}`
                    : colors.borderMuted
                    }`}
                >
                  {t("status.this-month")}
                </button>

                <div className="relative flex items-center" data-testid={fieldInputTestId("custom-date-range")}>
                  <label className={`text-sm font-medium ml-4 mr-2 ${colors.textSecondary}`} data-testid={fieldLabelTestId("custom-date-range")}>
                    {t("preferences.custom")}
                  </label>
                  <CustomDatePicker
                    selectsRange
                    selected={customDateRange.startDate}
                    startDate={customDateRange.startDate}
                    endDate={customDateRange.endDate}
                    onChange={([start, end]) => {
                      setDateType(DATE_TYPE.CUSTOM);
                      setCustomDateRange({ startDate: start, endDate: end });
                      setImportType(IMPORT_TYPE.DATE_RANGE);
                      setErrors(prev => ({ ...prev, dateRange: "", importType: "", projectCode: "" }));
                    }}
                    onFocus={() => {
                      setImportType(IMPORT_TYPE.DATE_RANGE);
                      setErrors(prev => ({ ...prev, dateRange: "", importType: "", projectCode: "" }));
                    }}
                    placeholderText={t("bulletin.select-date-range")}
                    className={`h-8 w-56 appearance-none rounded-lg border p-2 text-sm font-medium focus:outline-none ${colors.bgInput} ${colors.borderPrimary} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary}`}
                    dateFormat="yyyy-MM-dd"
                  />
                </div>
              </div>
              {errors.dateRange && (
                <p className={`text-sm ${colors.textDangerLight}`} data-testid={fieldErrorTestId("date-range")}>
                  {errors.dateRange}
                </p>
              )}
            </div>

            {/* PROJECT CODE */}
            <div
              data-testid={fieldContainerTestId("import-type-project-code")}
              className={`p-4 border rounded-lg space-y-3 ${importType === IMPORT_TYPE.PROJECT_CODE
                ? colors.borderSelectedOption
                : colors.borderDialogInactive
                }`}
            >
              <CustomRadio
                name="importType"
                value={IMPORT_TYPE.PROJECT_CODE}
                checked={importType === IMPORT_TYPE.PROJECT_CODE}
                onChange={() => {
                  setImportType(IMPORT_TYPE.PROJECT_CODE);
                  setErrors(prev => ({ ...prev, importType: "", projectCode: "", dateRange: "" }));
                }}
                label={t("preferences.fossid-project-code")}
              />

              <CustomTextarea
                data-testid={fieldInputTestId("project-codes")}
                value={projectCodes}
                onFocus={() => {
                  setImportType(IMPORT_TYPE.PROJECT_CODE);
                  setErrors(prev => ({ ...prev, projectCode: "", importType: "", projectCode: "", dateRange: "" }));
                }}
                onChange={(e) => {
                  const value = e.target.value;
                  const codes = splitProjectCodes(value);
                  const isDuplicate = hasDuplicateCodes(codes);
                  setProjectCodes(value);
                  setImportType(IMPORT_TYPE.PROJECT_CODE);
                  setErrors(prev => ({ ...prev, projectCode: "", importType: "", dateRange: "", projectCode: isDuplicate ? t("preferences.project-codes-duplicate") : "", }));
                }}
                placeholder={t("preferences.text-note-project-input")}
                rows={3}
                maxLength={10240}
                className={
                  importType !== IMPORT_TYPE.PROJECT_CODE ? "opacity-40" : ""
                }
              />
              {errors.projectCode && (
                <p className={`text-sm ${colors.textDangerLight}`} data-testid={fieldErrorTestId("project-codes")}>
                  {errors.projectCode}
                </p>
              )}
            </div>
          </section>
          {errors.importType && (
            <p className={`text-sm ${colors.textDangerLight} mt-2 pl-6`} data-testid={fieldErrorTestId("import-type")}>{errors.importType}</p>
          )}
        </div>

        {/* FOOTER */}
        <div className={`flex justify-end gap-3 p-6 border-t ${colors.borderPrimary}`} data-testid={`${dataTestId}-footer`}>
          <OutlineButton data-testid={`${dataTestId}-cancel-button`} onClick={onClose}>
            {t("common.cancel-button")}
          </OutlineButton>
          <PrimaryButton data-testid={`${dataTestId}-confirm-button`} onClick={handleSubmit} className="flex-shrink-0 relative">
            {t("common.import-button")}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );  
};

export default ImportProjectSettingModal;
